import React, { useEffect, useState, useMemo } from "react";
import { X, Save, RefreshCw, Calculator, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import {
  microLabel,
  accentBarSection,
  iconSize,
  card,
} from "../../../../lib/design-system";
import {
  getMarksSplit,
  bulkUpdateMarksSplit,
} from "../../../../services/program.service";

// Phase 6b — Internal vs external end-sem marks view + bulk editor.
//
// Why surfacing this matters for v1:
//   - NBA Criterion 3 / 4 reporting expects internal and external end-sem
//     marks to be reported separately. Distinction %, pass %, internal-vs-
//     external delta — these all feed the SAR.
//   - Existing grading UI uses a single `endTerm` number. This view doesn't
//     replace that — it adds a per-student split editor that derives endTerm
//     when both halves are set, so existing flows keep working.

const inputBase =
  "w-full px-2 py-1 text-sm rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-accent1 transition-colors";

const MarksSplitView = ({ courseId, courseCode, courseTitle, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Local edits, keyed by studentId
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getMarksSplit(courseId);
      setData(result);
      setEdits({});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load marks splits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [courseId]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && !saving) onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, saving]);

  const setEdit = (studentId, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [key]: value,
      },
    }));
  };

  const effective = (row, key) =>
    edits[row.studentId]?.[key] !== undefined
      ? edits[row.studentId][key]
      : row[key] != null
      ? String(row[key])
      : "";

  const dirtyStudentIds = useMemo(
    () => Object.keys(edits).filter((sid) => Object.keys(edits[sid]).length > 0),
    [edits]
  );

  const handleSave = async () => {
    if (dirtyStudentIds.length === 0) {
      toast("No changes to save", { icon: "ℹ️" });
      return;
    }
    setSaving(true);
    try {
      const splits = dirtyStudentIds.map((sid) => {
        const row = data.students.find((s) => s.studentId === sid);
        const internalStr = effective(row, "endTermInternal");
        const externalStr = effective(row, "endTermExternal");
        return {
          studentId: sid,
          endTermInternal: internalStr === "" ? null : Number(internalStr),
          endTermExternal: externalStr === "" ? null : Number(externalStr),
        };
      });
      const result = await bulkUpdateMarksSplit(courseId, splits);
      const ok = (result.updated || 0) + (result.created || 0);
      toast.success(
        `Saved · ${ok} student record(s) updated${
          result.errors?.length ? ` · ${result.errors.length} error(s)` : ""
        }`
      );
      if (result.errors?.length) {
        result.errors.forEach((e) =>
          toast.error(`Student ${e.studentId}: ${e.error}`)
        );
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => !saving && onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-5xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div>
              <div className={microLabel}>End-Sem Marks Split</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                {courseCode} {courseTitle ? `· ${courseTitle}` : ""}
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 max-w-xl">
                Internal = institute-set end-sem · External = university
                end-sem. When both are entered, the combined endTerm = internal
                + external, and totalMarks recomputes via the existing
                assessment plan weights.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                disabled={saving}
                className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw className={iconSize.md} />
              </button>
              <button
                onClick={() => onClose(false)}
                disabled={saving}
                className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-6 space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-10 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse"
                  />
                ))}
              </div>
            ) : error ? (
              <div className="p-6">
                <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              </div>
            ) : !data || data.students.length === 0 ? (
              <div className="p-6">
                <div className="p-6 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm">
                  <div className="font-bold text-gray-700 dark:text-gray-200">
                    No students enrolled in this course yet
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Enrol students (or run the demo seeder) before entering
                    end-sem splits.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Summary chips */}
                <div className="px-6 pt-5">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className={cn(card, "p-3")}>
                      <div className={microLabel}>Enrolled</div>
                      <div className="text-xl font-black mt-1 text-gray-900 dark:text-white">
                        {data.summary.totalEnrolled}
                      </div>
                    </div>
                    <div className={cn(card, "p-3")}>
                      <div className={microLabel}>Have Grading</div>
                      <div className="text-xl font-black mt-1 text-gray-900 dark:text-white">
                        {data.summary.haveGrading}
                      </div>
                    </div>
                    <div className={cn(card, "p-3")}>
                      <div className={microLabel}>Have Split</div>
                      <div className="text-xl font-black mt-1 text-accent1">
                        {data.summary.haveSplit}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Editable table */}
                <div className="overflow-x-auto px-6 pb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          Roll
                        </th>
                        <th className="text-left px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          Name
                        </th>
                        <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          Internal
                        </th>
                        <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          External
                        </th>
                        <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          = endTerm
                        </th>
                        <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          Mid
                        </th>
                        <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.students.map((row) => {
                        const internalStr = effective(row, "endTermInternal");
                        const externalStr = effective(row, "endTermExternal");
                        const internalNum =
                          internalStr === "" ? null : Number(internalStr);
                        const externalNum =
                          externalStr === "" ? null : Number(externalStr);
                        const derivedEndTerm =
                          internalNum != null && externalNum != null
                            ? internalNum + externalNum
                            : row.endTerm != null
                            ? row.endTerm
                            : null;
                        const isDirty =
                          edits[row.studentId] &&
                          Object.keys(edits[row.studentId]).length > 0;
                        return (
                          <tr
                            key={row.studentId}
                            className={cn(
                              "border-b border-gray-100 dark:border-gray-700/50",
                              isDirty &&
                                "bg-accent2/30 dark:bg-accent1/10"
                            )}
                          >
                            <td className="px-2 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                              {row.rollNumber || "—"}
                            </td>
                            <td className="px-2 py-2 text-gray-900 dark:text-white text-sm">
                              {row.name}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <input
                                type="number"
                                min={0}
                                step={0.5}
                                value={internalStr}
                                onChange={(e) =>
                                  setEdit(
                                    row.studentId,
                                    "endTermInternal",
                                    e.target.value
                                  )
                                }
                                placeholder="—"
                                className={cn(inputBase, "w-20 text-right")}
                              />
                            </td>
                            <td className="px-2 py-2 text-right">
                              <input
                                type="number"
                                min={0}
                                step={0.5}
                                value={externalStr}
                                onChange={(e) =>
                                  setEdit(
                                    row.studentId,
                                    "endTermExternal",
                                    e.target.value
                                  )
                                }
                                placeholder="—"
                                className={cn(inputBase, "w-20 text-right")}
                              />
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-sm">
                              {derivedEndTerm != null ? (
                                <span
                                  className={cn(
                                    "font-semibold",
                                    isDirty &&
                                      internalNum != null &&
                                      externalNum != null
                                      ? "text-accent1"
                                      : "text-gray-700 dark:text-gray-300"
                                  )}
                                >
                                  {derivedEndTerm}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right text-sm text-gray-500 dark:text-gray-400">
                              {row.midTerm ?? "—"}
                            </td>
                            <td className="px-2 py-2 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {row.totalMarks != null
                                ? row.totalMarks.toFixed
                                  ? row.totalMarks.toFixed(2)
                                  : row.totalMarks
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between flex-shrink-0 bg-gray-50 dark:bg-gray-900/30">
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Calculator className="h-3.5 w-3.5" />
              {dirtyStudentIds.length > 0
                ? `${dirtyStudentIds.length} student(s) modified`
                : "No changes yet"}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={saving}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold text-sm transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || dirtyStudentIds.length === 0}
                className={cn(
                  "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-accent1 text-white font-semibold text-sm transition-opacity",
                  saving || dirtyStudentIds.length === 0
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MarksSplitView;
